import React, { useState } from "react"
import { Alert, Badge, Button, Card, CardBody } from "reactstrap"
import { AddressDialog, ContactDialog } from "./DeliveryDialogs"
import { addressLines, fieldList, usedAtLabel } from "./delivery"
import { mainShipment } from "./shipmentLabels"
import { sheetEditable, shipmentShipped } from "./sheetState"
import { UNCONFIRMED_CHANGE } from "./useLogisticsOperation"
import OrganizerLink from "./OrganizerLink"

const contactSources = { organizer: "organizer", club: "club's public contact", previous: "used before", manual: "entered by hand" }
// The organizer whose profile the contact opens: only a contact copied from the organizer the event still has. In preparation, Events
// signals any change of organizer, to none included, by comparing ids; once the sheet has left preparation it signals nothing, so the
// contact must still carry the organizer's name. A company organizer's contact, named after its person, then stays text (Tudor 2026-10-12).
const contactOrganizer = ({ status, organizer }, contact, organizerChanged) => (contact?.source?.kind === "organizer" && organizer?.id
  && !organizerChanged && (sheetEditable(status) || contact.name === organizer.name) ? organizer : null)

const addressSource = ({ source }, timezone) => ({
  history: source.event && `Used at ${usedAtLabel(source.event, timezone)}`,
  publicAddress: "From the club's public address — complete it, then confirm it",
  entered: "Confirmed on this sheet",
}[source?.kind] || null)
const toComplete = fields => `To complete: ${fieldList(fields)}`

function Signal({ text, action, pending, onClick }) {
  return <Alert color="info" className="py-2 mt-2 mb-0 small">
    <i className="bx bx-info-circle mr-1" aria-hidden="true" />{text}{" "}
    <Button color="link" size="sm" className="p-0 align-baseline" disabled={pending} onClick={onClick}>{action}</Button>
  </Alert>
}

// The sheet's shipping address and contact person (CONTEXT „Adresa de expediere”, „Persoana de
// contact”): each a copy on the sheet, with what a courier still needs. While the sheet is in
// preparation, what changed on the event since they were chosen is signalled with the replacement
// Events offers, applied at one click and logged; a club or an organizer the event no longer has is
// reviewed by hand. Nothing changes by itself. Once shipped, they are the main shipment's: a correction
// after shipment (`correct`), with an optional reason (T5, Tudor 2026-10-06), unless Cargus generated the AWB (US 86–87).
export default function SheetDelivery({ eventId, sheet, timezone, pending, changeDelivery, correct }) {
  const { address, contact, missing, warnings, signals, place } = sheet.delivery
  const editable = sheetEditable(sheet.status)
  const main = mainShipment(sheet)
  const correctable = shipmentShipped(main?.status) && main.awbSource !== "generated"
  const [dialog, setDialog] = useState(null)
  const [failure, setFailure] = useState(null)
  const open = next => { setFailure(null); setDialog(next) }
  const close = () => { setFailure(null); setDialog(null) }
  const submit = body => (correctable ? correct : changeDelivery)(body).then(result => {
    if (!result || result.status === "cancelled") return
    if (result.status === "saved") close()
    else setFailure(result.status === "unknown" ? UNCONFIRMED_CHANGE : result.message)
  })
  const replace = body => { setFailure(null); submit(body) }
  const signalOf = kind => signals.find(signal => signal.kind === kind)
  const clubChanged = signalOf("clubChanged")
  const clubAddressChanged = signalOf("clubAddressChanged")
  const organizerChanged = signalOf("organizerChanged")
  const eventContactChanged = signalOf("eventContactChanged")
  const dialogState = { eventId, timezone, pending, failure, onClose: close, onSubmit: submit }

  return <div className="row" id="logistics-delivery" role="region" aria-label="Delivery">
    <div className="col-lg-6 d-flex">
      <Card tag="section" aria-label="Delivery address" className="flex-grow-1"><CardBody>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h4 className="card-title">Delivery address{" "}
              {address?.confirmed && <Badge color="soft-success">Confirmed</Badge>}
              {address?.marks.includes("toConfirm") && <Badge color="soft-warning">To confirm</Badge>}
            </h4>
            {address ? <>
              <div><strong>{address.recipient || "No recipient"}</strong> <span className="text-muted small">(recipient)</span></div>
              {addressLines(address).map(line => <div key={line}>{line}</div>)}
              {address.details && <div className="text-muted small">{address.details}</div>}
              {addressSource(address, timezone) && <div className="text-muted small">{addressSource(address, timezone)}</div>}
              <div className="text-muted small">{address.club ? `Delivery address of ${address.club.name}` : "Alternative address, not a club's delivery address"}</div>
            </> : <p className="text-muted mb-1">No address yet: choose one used before or enter it.</p>}
            {missing.address.length > 0 && <div className="text-danger small">{toComplete(missing.address)}</div>}
            {warnings.includes("postalCodeRecommended") && <div className="text-warning small">Postal code recommended</div>}
          </div>
          {editable && <Button color="light" size="sm" disabled={pending} onClick={() => open("address")}>
            {!address ? "Add address" : address.confirmed ? "Change" : "Review"}
          </Button>}
          {correctable && <Button color="light" size="sm" disabled={pending} onClick={() => open("address")}>Correct</Button>}
        </div>
        {clubChanged?.club && <Signal pending={pending} action={`Use the address of ${clubChanged.club.name}`}
          text={`The event's club changed to ${clubChanged.club.name}${clubChanged.previous ? ` (was ${clubChanged.previous.name})` : ""}.`}
          onClick={() => replace({ action: "setAddress", address: clubChanged.address, club: clubChanged.club.id, confirm: clubChanged.confirmed,
            ...(clubChanged.confirmed ? { from: clubChanged.from } : {}), signal: "clubChanged" })} />}
        {clubChanged && !clubChanged.club && <Signal pending={pending} action="Review the address" onClick={() => open("address")}
          text={`The event no longer has a club${clubChanged.previous ? ` (was ${clubChanged.previous.name})` : ""}.`} />}
        {clubAddressChanged && address?.club && <Signal pending={pending} text={`The club's public address changed to ${clubAddressChanged.place.name}.`}
          action="Use the new club address" onClick={() => replace({ action: "setAddress", address: clubAddressChanged.address, club: address.club.id,
            confirm: false, signal: "clubAddressChanged" })} />}
      </CardBody></Card>
    </div>
    <div className="col-lg-6 d-flex">
      <Card tag="section" aria-label="Contact person" className="flex-grow-1"><CardBody>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h4 className="card-title">Contact person</h4>
            {contact ? <>
              <div><strong><OrganizerLink organizer={contactOrganizer(sheet, contact, organizerChanged)}>{contact.name || "No name"}</OrganizerLink></strong> <span className="text-muted small">· {contactSources[contact.source?.kind] || "entered by hand"}</span></div>
              <div>
                <i className="bx bx-phone mr-1" aria-hidden="true" />{contact.phone || <span className="text-danger">no phone</span>}
                <i className="bx bx-envelope ml-3 mr-1" aria-hidden="true" />{contact.email || <span className="text-danger">no email</span>}
              </div>
              {contact.courierPhone && contact.courierPhone !== contact.phone && <div className="text-muted small">The courier dials {contact.courierPhone}</div>}
            </> : <p className="text-muted mb-1">No contact yet: choose one or enter it.</p>}
            {missing.contact.length > 0 && <div className="text-danger small">{toComplete(missing.contact)}</div>}
            <div className="text-muted small">Required with a courier: name, phone, email{address?.country && address.country !== "RO" ? " (a phone with the country prefix)" : ""}</div>
          </div>
          {editable && <Button color="light" size="sm" disabled={pending} onClick={() => open("contact")}>{contact ? "Edit" : "Add contact"}</Button>}
          {correctable && <Button color="light" size="sm" disabled={pending} onClick={() => open("contact")}>Correct</Button>}
        </div>
        {organizerChanged?.organizer && <Signal pending={pending}
          text={<>The event's organizer changed to <OrganizerLink organizer={organizerChanged.organizer}>{organizerChanged.organizer.name}</OrganizerLink>.</>}
          action={`Use ${organizerChanged.organizer.name} as the contact`}
          onClick={() => replace({ action: "setContact", contact: organizerChanged.contact, source: "organizer", signal: "organizerChanged" })} />}
        {organizerChanged && !organizerChanged.organizer && <Signal pending={pending} action="Review the contact" onClick={() => open("contact")}
          text={`The event no longer has an organizer${organizerChanged.previous ? ` (was ${organizerChanged.previous.name})` : ""}.`} />}
        {eventContactChanged && <Signal pending={pending} action="Update the contact"
          text={`The event's phone or email changed: ${eventContactChanged.contact.phone || "no phone"} · ${eventContactChanged.contact.email || "no email"}.`}
          onClick={() => replace({ action: "setContact", contact: eventContactChanged.contact, source: "organizer", signal: "eventContactChanged" })} />}
      </CardBody></Card>
    </div>
    {failure && !dialog && <div className="col-12"><Alert color="danger">{failure}</Alert></div>}
    {dialog === "address" && <AddressDialog current={address} place={place} correction={correctable} {...dialogState} />}
    {dialog === "contact" && <ContactDialog current={contact} country={address?.country || ""} correction={correctable} {...dialogState} />}
  </div>
}
